import type {
    CatalogMaterial,
    MaterialRejectionReason,
    OverDeliveryDecision,
    ReceiptQcVerdict,
    RejectionAction,
    ReceiveLine,
    VendorCatalog,
} from '@api/vendor-portal';
import type { OptionsFilter } from '@mantine/core';

/**
 * The "+ Add supplier" row of the supplier picker - a value no vendor can have, so picking it
 * opens the form instead of being stored. Same device as the prefix picker on a production
 * order.
 */
export const ADD_SUPPLIER_VALUE = '__add_supplier__';

/** The default search, except that the way out never filters away with the list. */
export const keepAddSupplier: OptionsFilter = ({ options, search }) => {
    const needle = search.trim().toLowerCase();
    return options.filter(
        (option) =>
            !('value' in option) ||
            option.value === ADD_SUPPLIER_VALUE ||
            option.label.toLowerCase().includes(needle),
    );
};

/**
 * One line of a delivery as it is being typed.
 *
 * EVERY FIELD IS A STRING, including the quantity. A grid cell holds what somebody has typed so
 * far, and "12." or "" are states a number cannot represent — coercing on every keystroke fights
 * the person entering it. Numbers are parsed once, on submit.
 */
export interface ReceiveRow {
    /** Client-side only, so rows survive reordering and removal. */
    id: string;
    itemId: string;
    quantity: string;
    batchCode: string;
    supplierId: string;
    manufacturingDate: string;
    expiryDate: string;
    /**
     * The gate verdict, or '' while nobody has picked one. Held as text like every other cell
     * - a pasted file lands here before it is checked - and validated in `rowIssues`.
     */
    qcVerdict: string;
    /** Only meaningful with SOME_REJECTED. Text, for the same reason `quantity` is. */
    rejectedQuantity: string;
    rejectionReason: string;
    /** Where the refused quantity goes - RETURN, SCRAP or KEEP. Picked, never defaulted. */
    rejectionAction: string;
    purchaseOrderId: string;
    notes: string;
    /**
     * Per unit, excluding GST. Only ever filled on the brand's side - the manufacturer's
     * grid has no such column, and the server ignores the field on that path.
     */
    unitCost: string;
    /**
     * What the brand said to do with this item's quantity above the order: PAY or FREE, or ''
     * while nothing has been said. Set by the dialog on save, never typed; brand's side only.
     */
    overDeliveryDecision: string;
}

/**
 * A row restored from disk, repaired into a row this build understands.
 *
 * A SAVED DRAFT IS UNTRUSTED INPUT. It was written by whatever version of this screen the
 * operator last had open, which may be weeks old, and it is deserialised straight back into
 * state — so it is exactly like an API response from an older server: the same discipline
 * applies, and it was not being applied.
 *
 * THIS IS NOT HYPOTHETICAL. `unitCost` was added to this row on 11 August; a draft saved before
 * then rehydrates without it, and `toLines` calls `row.unitCost.trim()` on submit. The grid
 * renders perfectly, reports the line as ready, and throws "Cannot read properties of undefined"
 * the moment somebody presses the button - because `unitCost` is the one trimmed field that
 * render-time validation never touches, so nothing fails earlier and louder.
 *
 * FIELD BY FIELD, RATHER THAN A SPREAD OVER DEFAULTS, because the missing key is inside an array
 * of rows: `{ ...blankRow(), ...stored }` at the draft level takes the stored rows wholesale and
 * repairs nothing. Each row has to be walked.
 *
 * REPAIRED RATHER THAN DISCARDED. Dropping a draft whose shape we no longer recognise would also
 * stop the crash, and would throw away work somebody typed - possibly the delivery standing in
 * front of them. Anything absent gets its blank value, which is what an untouched field would
 * have held anyway.
 */
export function hydrateRow(stored: unknown): ReceiveRow {
    // `qcDone` is the tick drafts carried before the verdict existed - read for repair only.
    const row = (stored ?? {}) as Partial<Record<keyof ReceiveRow | 'qcDone', unknown>>;
    const text = (value: unknown) => (typeof value === 'string' ? value : '');
    const fresh = blankRow();

    return {
        // Kept if it looks like one, so React's keys survive a reorder across a reload.
        id: text(row.id) || fresh.id,
        itemId: text(row.itemId),
        quantity: text(row.quantity),
        unitCost: text(row.unitCost),
        batchCode: text(row.batchCode),
        supplierId: text(row.supplierId),
        manufacturingDate: text(row.manufacturingDate),
        expiryDate: text(row.expiryDate),
        // A draft from before the verdict existed carried a `qcDone` tick. True is the one
        // answer it could give, so it becomes that; anything else waits on a verdict.
        qcVerdict: text(row.qcVerdict) || (row.qcDone === true ? 'ALL_APPROVED' : ''),
        rejectedQuantity: text(row.rejectedQuantity),
        rejectionReason: text(row.rejectionReason),
        rejectionAction: text(row.rejectionAction),
        purchaseOrderId: text(row.purchaseOrderId),
        notes: text(row.notes),
        overDeliveryDecision: text(row.overDeliveryDecision),
    };
}

export function blankRow(): ReceiveRow {
    return {
        id: `row-${Math.random().toString(36).slice(2, 10)}`,
        itemId: '',
        quantity: '',
        unitCost: '',
        batchCode: '',
        supplierId: '',
        manufacturingDate: '',
        expiryDate: '',
        qcVerdict: '',
        rejectedQuantity: '',
        rejectionReason: '',
        rejectionAction: '',
        purchaseOrderId: '',
        notes: '',
        overDeliveryDecision: '',
    };
}

/** A row nobody has touched. Trailing blanks are normal in a grid and are not errors. */
export function isBlank(row: ReceiveRow): boolean {
    return (
        !row.itemId &&
        !row.quantity.trim() &&
        !row.batchCode.trim() &&
        !row.supplierId &&
        !row.purchaseOrderId.trim() &&
        !row.notes.trim()
    );
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * The verdict, in the words somebody at the dock would say. No default: the empty state is
 * the point, and the row cannot be saved from it.
 */
export const QC_VERDICT_OPTIONS: Array<{ value: ReceiptQcVerdict; label: string }> = [
    { value: 'ALL_APPROVED', label: 'All approved' },
    { value: 'SOME_REJECTED', label: 'Some rejected' },
    { value: 'ALL_REJECTED', label: 'All rejected' },
];

/**
 * Why, grouped by whose loss it is. Where the goods go is asked separately - see
 * REJECTION_ACTION_OPTIONS.
 */
export const REJECTION_REASON_OPTIONS: Array<{
    group: string;
    items: Array<{ value: MaterialRejectionReason; label: string }>;
}> = [
    {
        group: 'Supplier\u2019s fault',
        items: [
            { value: 'DAMAGED_IN_TRANSIT', label: 'Damaged in transit' },
            { value: 'WRONG_PRINT_OR_ITEM', label: 'Wrong print / wrong item' },
            { value: 'SHORT_DATED', label: 'Short-dated / expired' },
            { value: 'QUANTITY_SHORT', label: 'Quantity short of the note' },
            { value: 'OTHER_SUPPLIER', label: 'Other (supplier)' },
        ],
    },
    {
        group: 'Site\u2019s fault',
        items: [
            { value: 'DAMAGED_AT_SITE', label: 'Damaged at the site' },
            { value: 'OTHER_SITE', label: 'Other (site)' },
        ],
    },
];

/** Where the refused quantity goes. No default: a missed pick must stop the row, not guess. */
export const REJECTION_ACTION_OPTIONS = [
    { value: 'RETURN', label: 'Return' },
    { value: 'SCRAP', label: 'Scrap' },
    { value: 'KEEP', label: 'Keep at site' },
] as const;

/** The site cannot send its own damage back to the supplier. */
export const SITE_REASONS: ReadonlySet<string> = new Set(
    REJECTION_REASON_OPTIONS[1].items.map((item) => item.value),
);

/** The actions open to a reason: Return is withheld from the site's own. */
export function actionsFor(reason: string, onTransfer = false) {
    // Goods on a stock transfer came from another location (one of the brand's warehouses or a
    // vendor site holding its stock), not from a supplier: there is no supplier to return them to.
    return REJECTION_ACTION_OPTIONS.filter(
        (option) => option.value !== 'RETURN' || (!onTransfer && !SITE_REASONS.has(reason)),
    );
}

/** The two reasons that say nothing on their own, and so the two that demand a note. */
export const REASONS_NEEDING_NOTES: ReadonlySet<string> = new Set(['OTHER_SUPPLIER', 'OTHER_SITE']);

const VERDICT_VALUES = new Set<string>(QC_VERDICT_OPTIONS.map((option) => option.value));
const REASON_VALUES = new Set<string>(
    REJECTION_REASON_OPTIONS.flatMap((group) => group.items.map((item) => item.value)),
);

/**
 * What is wrong with a row, in the words the person needs.
 *
 * DELIBERATELY THE SAME RULES THE SERVER ENFORCES, and deliberately not the authority. The
 * server decides; this exists so that somebody finds out at the row they are typing rather than
 * after submitting forty of them. Where the two could drift, the server wins and says so.
 */
export function rowIssues(row: ReceiveRow, catalog: VendorCatalog | undefined): string[] {
    if (isBlank(row)) return [];

    const issues: string[] = [];
    const material = catalog?.materials.find((m) => m.itemId === row.itemId);

    if (!row.itemId) issues.push('Pick an item.');
    else if (catalog && !material) issues.push('Not a material on this site’s list.');

    const quantity = Number(row.quantity);
    if (!row.quantity.trim()) issues.push('Enter a quantity.');
    else if (!Number.isFinite(quantity) || quantity <= 0) {
        issues.push('Quantity must be above zero.');
    }

    /*
     * THE VERDICT IS NOT INFERRED FROM THE QUANTITY. "Received 5,000" is not "approved 5,000";
     * somebody has to say so, and a row that has not said cannot be saved. The same four rules
     * the server enforces, here so they land at the row rather than after forty of them.
     */
    if (!VERDICT_VALUES.has(row.qcVerdict)) {
        issues.push('Pick a QC verdict — recording this line means the site checked it.');
    } else {
        const rejected = Number(row.rejectedQuantity);
        const some = row.qcVerdict === 'SOME_REJECTED';
        if (some && !(row.rejectedQuantity.trim() && rejected > 0 && rejected < quantity)) {
            issues.push('Rejected must be above 0 and below what was received.');
        }
        const anyRejected = some || row.qcVerdict === 'ALL_REJECTED';
        if (anyRejected && !REASON_VALUES.has(row.rejectionReason)) {
            issues.push('Pick a reason for the rejection.');
        }
        if (anyRejected && REASONS_NEEDING_NOTES.has(row.rejectionReason) && !row.notes.trim()) {
            issues.push('“Other” needs a note saying what was wrong.');
        }
        if (anyRejected && !actionsFor(row.rejectionReason).some((option) => option.value === row.rejectionAction)) {
            issues.push(
                row.rejectionAction === 'RETURN'
                    ? 'Damage at the site cannot go back to the supplier — scrap it or keep it.'
                    : 'Pick what happens to the rejected stock: return, scrap or keep at site.',
            );
        }
    }

    const code = row.batchCode.trim();

    if (material && isTracked(catalog, material) && !code) {
        issues.push('This brand traces this item by batch — enter the code on the goods.');
    }

    // NONE OF THIS ON THE SITE'S OWN DOOR. The site is handed no supplier and asked for none;
    // the server attributes the receipt. These three rules belong to the brand's picker.
    if (!catalog?.suppliersHidden) {
        // A code without a supplier is not an identity: it is only unique to whoever printed it.
        if (code && !row.supplierId) issues.push('Say who supplied this batch.');

        // The picker only offers mapped suppliers, but a pasted file can name anyone.
        if (material && row.supplierId && !material.supplierIds.includes(row.supplierId)) {
            issues.push('Not a supplier of this item.');
        }

        // A traced item needs a batch, a batch needs a supplier, and a supplier has to be
        // mapped: with nobody mapped the row can never become valid, and saying so beats a
        // dead end.
        if (material && isTracked(catalog, material) && material.supplierIds.length === 0) {
            issues.push('No supplier is mapped to this item yet. Add one from the Supplier box.');
        }
    }

    for (const [label, value] of [
        ['Manufactured', row.manufacturingDate],
        ['Expiry', row.expiryDate],
    ] as const) {
        if (value.trim() && !ISO_DATE.test(value.trim())) {
            issues.push(`${label} date should look like 2027-01-31.`);
        }
    }

    return issues;
}

/** How `orderIssues` words a quantity above what the order still owes; the brand's door reads it back. */
const OVER_ORDER_ISSUE = 'Quantity is more than this PO still owes';

/** Whether an issue is the over-order one - the only issue the brand may answer rather than fix. */
export const isOverOrderIssue = (issue: string): boolean => issue.startsWith(OVER_ORDER_ISSUE);

/**
 * What a delivery received against a purchase order may not do: bring an item the order does
 * not have, or more of one than it still owes.
 *
 * SUMMED PER ITEM FIRST, and every row of an over-filled item is marked, so two rows of one
 * item cannot each fit under the limit and together go over it. What arrived is what counts -
 * a refusal at the gate does not make room - which is the same rule the server applies.
 *
 * Worded so the quantity cell lights up: the grid marks a cell by the issue text it carries.
 */
export function orderIssues(
    rows: ReceiveRow[],
    remainingByItem: ReadonlyMap<string, number>,
    unitFor: (itemId: string) => string,
): Map<string, string[]> {
    const issues = new Map<string, string[]>();
    const add = (rowId: string, issue: string) =>
        issues.set(rowId, [...(issues.get(rowId) ?? []), issue]);

    const arriving = quantityByItem(rows);

    for (const row of rows) {
        if (isBlank(row) || !row.itemId) continue;
        const remaining = remainingByItem.get(row.itemId);
        if (remaining === undefined) {
            add(row.id, 'This item is not on the purchase order, or is already received in full.');
            continue;
        }
        const total = arriving.get(row.itemId) ?? 0;
        if (total > remaining + 1e-6) {
            const unit = unitFor(row.itemId);
            const amount = (value: number) => `${formatRemaining(value)}${unit ? ` ${unit}` : ''}`;
            add(
                row.id,
                `${OVER_ORDER_ISSUE}: ${amount(total)} entered, only ${amount(remaining)} left (${amount(total - remaining)} over).`,
            );
        }
    }
    return issues;
}

/** What this delivery brings of each item, summed across its rows. */
export function quantityByItem(rows: ReceiveRow[]): Map<string, number> {
    const arriving = new Map<string, number>();
    for (const row of rows) {
        if (isBlank(row) || !row.itemId) continue;
        const quantity = Number(row.quantity);
        if (Number.isFinite(quantity) && quantity > 0) {
            arriving.set(row.itemId, (arriving.get(row.itemId) ?? 0) + quantity);
        }
    }
    return arriving;
}

/** 13.4, not 13.399999999999999. */
function formatRemaining(value: number): string {
    return String(Math.round(value * 1000) / 1000);
}

export function isTracked(
    catalog: VendorCatalog | undefined,
    material: CatalogMaterial,
): boolean {
    return catalog?.batchTracking?.[material.materialType] === 'full';
}

/** Whether the brand traces anything at all. When it does not, the batch columns are noise. */
export function tracesAnything(
    catalog: { batchTracking?: Partial<Record<string, string>> | null } | undefined,
): boolean {
    return Object.values(catalog?.batchTracking ?? {}).some((mode) => mode === 'full');
}

/**
 * The fields a column can be, and the names a person might write for each.
 *
 * Aliases rather than one exact spelling, because the template is a starting point and what
 * comes back has been through somebody's spreadsheet. Everything here is a name a header could
 * plausibly carry; nothing here is a guess at what a DATA cell means.
 */
const FIELD_ALIASES: Record<string, keyof ReceiveRow> = {
    'item': 'itemId',
    'item id': 'itemId',
    'item code': 'itemId',
    'sku': 'itemId',
    'material': 'itemId',
    'material id': 'itemId',
    'code': 'itemId',
    'product': 'itemId',
    'quantity': 'quantity',
    'qty': 'quantity',
    'received': 'quantity',
    'batch': 'batchCode',
    'batch code': 'batchCode',
    'lot': 'batchCode',
    'lot code': 'batchCode',
    'supplier': 'supplierId',
    'supplier id': 'supplierId',
    'vendor': 'supplierId',
    // 'made' kept alongside 'manufactured': the template used to say Made, and a file
    // exported from it before the rename must still import.
    'made': 'manufacturingDate',
    'mfg': 'manufacturingDate',
    'manufactured': 'manufacturingDate',
    'manufacturing date': 'manufacturingDate',
    'expires': 'expiryDate',
    'expiry': 'expiryDate',
    'expiry date': 'expiryDate',
    'best before': 'expiryDate',
    'qc': 'qcVerdict',
    'qc verdict': 'qcVerdict',
    'verdict': 'qcVerdict',
    'rejected': 'rejectedQuantity',
    'rejected qty': 'rejectedQuantity',
    'rejected quantity': 'rejectedQuantity',
    'reason': 'rejectionReason',
    'rejection reason': 'rejectionReason',
    'action': 'rejectionAction',
    'rejection action': 'rejectionAction',
    'po': 'purchaseOrderId',
    'purchase order': 'purchaseOrderId',
    'po number': 'purchaseOrderId',
    'notes': 'notes',
    'remarks': 'notes',
    'comment': 'notes',
    'comments': 'notes',
};

/**
 * The columns, in the order the grid shows them — the order a positional fill assumes.
 *
 * The three QC columns sit right after the quantity on every grid, traced or not: the verdict
 * is the site's now, given at the gate, and a file that leaves them blank lands as rows waiting
 * on a verdict - never as rows approved by omission.
 */
function positionalOrder(withBatchColumns: boolean, withSupplier = true): Array<keyof ReceiveRow> {
    const qc = ['qcVerdict', 'rejectedQuantity', 'rejectionReason', 'rejectionAction'] as const;
    if (!withBatchColumns) return ['itemId', 'quantity', ...qc, 'purchaseOrderId', 'notes'];

    return [
        'itemId',
        'quantity',
        ...qc,
        'batchCode',
        // The site's template carries no supplier column: it is not asked for one.
        ...(withSupplier ? (['supplierId'] as const) : []),
        'manufacturingDate',
        'expiryDate',
        'purchaseOrderId',
        'notes',
    ];
}

/** The header row of a downloadable template, in the same order. */
const HEADER_LABELS: Record<string, string> = {
    itemId: 'Item',
    quantity: 'Quantity',
    batchCode: 'Batch',
    supplierId: 'Supplier',
    manufacturingDate: 'Manufactured (YYYY-MM-DD)',
    expiryDate: 'Expires (YYYY-MM-DD)',
    qcVerdict: 'QC verdict (all approved / some rejected / all rejected)',
    rejectedQuantity: 'Rejected',
    rejectionReason: 'Reason',
    rejectionAction: 'Action (return / scrap / keep at site)',
    purchaseOrderId: 'PO',
    notes: 'Notes',
};

/** A template carrying this brand's columns and nothing else, headers only. */
export function templateCsv(
    withBatchColumns: boolean,
    withSupplier = true,
    /** Item names to pre-fill one row each - the lines a purchase order still owes. */
    items: string[] = [],
): string {
    const fields = positionalOrder(withBatchColumns, withSupplier);
    const header = `${fields.map((field) => HEADER_LABELS[field]).join(',')}\n`;
    // The name in the item column, quoted, and every other cell left for the person to fill.
    const blanks = ','.repeat(fields.length - 1);
    return header + items.map((name) => `"${name.replace(/"/g, '""')}"${blanks}\n`).join('');
}

/**
 * A header reduced to the word it is about.
 *
 * A parenthesised hint is how a spreadsheet says what it wants — "Quantity (kg)", "Expires
 * (YYYY-MM-DD)" — and the template hands out exactly that. Stripping it here is what lets the
 * template we give somebody come back in through the importer we wrote, rather than needing
 * every parenthesised variant spelled out as its own alias.
 */
function headerKey(cell: string): string {
    return cell.replace(/\(.*?\)/g, '').trim().toLowerCase();
}

/**
 * Split delimited text into cells, respecting quotes.
 *
 * Hand-rolled rather than pulling in a parser, but NOT hand-waved: a value may contain the
 * delimiter, a line break, or a quote of its own, and every one of those appears in real notes
 * fields. Splitting on the delimiter would silently shear a row in half and put half a comment
 * into the next column, which is the kind of corruption nobody notices until the ledger is wrong.
 *
 * Follows the spreadsheet convention: a field wrapped in quotes may contain anything, and a
 * doubled quote inside it is one literal quote.
 */
export function parseDelimited(text: string, delimiter: string): string[][] {
    const rows: string[][] = [];
    let row: string[] = [];
    let value = '';
    let quoted = false;

    const endValue = () => {
        row.push(value.trim());
        value = '';
    };
    const endRow = () => {
        endValue();
        if (row.some((cell) => cell.length > 0)) rows.push(row);
        row = [];
    };

    for (let i = 0; i < text.length; i += 1) {
        const char = text[i];

        if (quoted) {
            if (char !== '"') {
                value += char;
            } else if (text[i + 1] === '"') {
                value += '"';
                i += 1;
            } else {
                quoted = false;
            }
            continue;
        }

        if (char === '"') quoted = true;
        else if (char === delimiter) endValue();
        else if (char === '\n') endRow();
        else if (char !== '\r') value += char;
    }

    endRow();
    return rows;
}

/**
 * Whether the first line names columns rather than carrying data.
 *
 * Half of it having a recognisable field name is a deliberately loose bar to clear on purpose
 * and a hard one to clear by accident: a real line is item codes, numbers and dates, and none
 * of those spell "quantity".
 */
function looksLikeHeader(cells: string[]): boolean {
    const filled = cells.filter((cell) => cell.length > 0);
    if (filled.length === 0) return false;
    const known = filled.filter((cell) => FIELD_ALIASES[headerKey(cell)]).length;
    return known * 2 >= filled.length;
}

/**
 * Turn a grid of cells into rows.
 *
 * BY HEADER WHEN THERE IS ONE, by position otherwise. A file that came back from the template
 * still works when somebody has moved a column or deleted one they did not need, which is the
 * first thing that happens to a template. A block copied out of the middle of a spreadsheet has
 * no header, and there position is all the information there is.
 *
 * Short rows are fine either way — item and quantity alone is the common case, and the rest
 * stays empty rather than being invented.
 */
export function cellsToRows(
    cells: string[][],
    withBatchColumns: boolean,
    withSupplier = true,
): ReceiveRow[] {
    if (cells.length === 0) return [];

    let order: Array<keyof ReceiveRow | undefined>;
    let body = cells;

    if (looksLikeHeader(cells[0])) {
        order = cells[0].map((cell) => FIELD_ALIASES[headerKey(cell)]);
        body = cells.slice(1);
    } else {
        order = positionalOrder(withBatchColumns, withSupplier);
    }

    return body.map((line) => {
        const row = blankRow();
        line.forEach((value, index) => {
            const field = order[index];
            if (!field) return;
            if (field !== 'id') row[field] = value;
        });
        return row;
    });
}

/** A block copied out of a spreadsheet, which arrives tab-separated. */
export function parsePastedBlock(
    text: string,
    withBatchColumns: boolean,
    withSupplier = true,
): ReceiveRow[] {
    return cellsToRows(parseDelimited(text, '\t'), withBatchColumns, withSupplier);
}

/** An uploaded file. Same path as a paste, one delimiter later. */
export function parseCsv(text: string, withBatchColumns: boolean, withSupplier = true): ReceiveRow[] {
    return cellsToRows(parseDelimited(text, ','), withBatchColumns, withSupplier);
}

/**
 * Turn what somebody wrote into what the pickers hold.
 *
 * A person filling a template writes "Acme Mills", not a vendor id, and the grid's pickers are
 * keyed by id. Resolving the obvious cases here means an imported file arrives ready rather
 * than as a wall of red.
 *
 * NOTHING IS GUESSED. An exact match on the id, then an exact match on the name, then it is
 * left alone: an item that resolves to nothing keeps the text so the row says what was written
 * and why it is wrong, and a supplier that resolves to nothing is cleared, because a Select
 * cannot show a value that is not in its list and a half-resolved supplier is worse than an
 * empty one the row is already flagging.
 */
export function resolveAgainstCatalog(
    rows: ReceiveRow[],
    catalog: VendorCatalog | undefined,
): { rows: ReceiveRow[]; unresolvedSuppliers: number } {
    if (!catalog) return { rows, unresolvedSuppliers: 0 };

    const itemByKey = new Map<string, string>();
    for (const material of catalog.materials) {
        itemByKey.set(material.itemId.toLowerCase(), material.itemId);
        itemByKey.set(material.name.toLowerCase(), material.itemId);
    }

    const supplierByKey = new Map<string, string>();
    for (const supplier of catalog.suppliers) {
        supplierByKey.set(supplier.vendorId.toLowerCase(), supplier.vendorId);
        supplierByKey.set(supplier.name.toLowerCase(), supplier.vendorId);
    }

    // "All approved" and "Some rejected" as a person writes them, or the code itself. A
    // value that resolves to nothing is cleared, same as a supplier: a Select cannot show it,
    // and the row's own issue text says a verdict is still needed.
    const verdictByKey = new Map<string, string>();
    for (const option of QC_VERDICT_OPTIONS) {
        verdictByKey.set(option.value.toLowerCase(), option.value);
        verdictByKey.set(option.label.toLowerCase(), option.value);
    }
    const actionByKey = new Map<string, string>();
    for (const option of REJECTION_ACTION_OPTIONS) {
        actionByKey.set(option.value.toLowerCase(), option.value);
        actionByKey.set(option.label.toLowerCase(), option.value);
    }
    const reasonByKey = new Map<string, string>();
    for (const option of REJECTION_REASON_OPTIONS.flatMap((group) => group.items)) {
        reasonByKey.set(option.value.toLowerCase(), option.value);
        reasonByKey.set(option.label.toLowerCase(), option.value);
    }

    let unresolvedSuppliers = 0;

    const resolved = rows.map((row) => {
        const itemId = itemByKey.get(row.itemId.trim().toLowerCase()) ?? row.itemId;

        let supplierId = '';
        if (row.supplierId.trim()) {
            supplierId = supplierByKey.get(row.supplierId.trim().toLowerCase()) ?? '';
            if (!supplierId) unresolvedSuppliers += 1;
        }

        const qcVerdict = verdictByKey.get(row.qcVerdict.trim().toLowerCase()) ?? '';
        const rejectionReason = reasonByKey.get(row.rejectionReason.trim().toLowerCase()) ?? '';
        const rejectionAction = actionByKey.get(row.rejectionAction.trim().toLowerCase()) ?? '';

        return { ...row, itemId, supplierId, qcVerdict, rejectionReason, rejectionAction };
    });

    return { rows: resolved, unresolvedSuppliers };
}

/** The rows that will actually be sent — blanks dropped, strings turned into what the API wants. */
/**
 * `currency` applies to every priced line: one note, one currency. Lines with no rate carry
 * neither, so an uncosted receipt stays uncosted rather than acquiring a currency for nothing.
 */
export function toLines(rows: ReceiveRow[], currency = 'INR'): ReceiveLine[] {
    return rows.filter((row) => !isBlank(row)).map((row) => ({
        itemId: row.itemId,
        quantity: Number(row.quantity),
        batchCode: row.batchCode.trim() || undefined,
        supplierId: row.supplierId || undefined,
        manufacturingDate: row.manufacturingDate.trim() || undefined,
        expiryDate: row.expiryDate.trim() || undefined,
        qcVerdict: row.qcVerdict as ReceiptQcVerdict,
        // Only a partial refusal carries a count; the other two verdicts imply theirs.
        rejectedQuantity:
            row.qcVerdict === 'SOME_REJECTED' ? Number(row.rejectedQuantity) : undefined,
        rejectionReason: (row.rejectionReason || undefined) as MaterialRejectionReason | undefined,
        rejectionAction: (row.rejectionAction || undefined) as RejectionAction | undefined,
        purchaseOrderId: row.purchaseOrderId.trim() || undefined,
        overDeliveryDecision: (row.overDeliveryDecision || undefined) as OverDeliveryDecision | undefined,
        notes: row.notes.trim() || undefined,
        // Left undefined rather than 0 when blank: an unpriced receipt is uncosted, not free.
        unitCost: row.unitCost.trim() === '' ? undefined : Number(row.unitCost),
        currency: row.unitCost.trim() === '' ? undefined : currency,
    }));
}
